import type { BibleBookmark } from '@/types/bookmark';
import type { BibleHighlight } from '@/types/highlight';
import type {
  AcceptedTombstoneV1,
  CompleteBookmarkDocument,
  CompleteHighlightDocument,
  CompleteLiveDocument,
  CompletePrayerDocument,
  CompleteScriptureAnchor,
  ConflictRecordV1,
  ContentEntityType,
  ContentMutationV1,
  DurablePendingContentTupleV1,
  HexSha256,
  LiveMutationV1,
  OrdinaryContentEnvelopeV1,
  StagedConflictV1,
} from '@/types/content-lifecycle';
import type { Prayer, ScriptureAnchor } from '@/types/prayer';
import { progressEntryIds } from '../progress-entry-identity';
import { CANONICAL_BOOKS } from '../bsb-types';
import { bsbChapterVerseCount } from './canonical-verse-counts';

const encoder = new TextEncoder();
const decoder = new TextDecoder('utf-8', { fatal: true });

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
const UUID_V4_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
const HEX_SHA256_RE = /^[0-9a-f]{64}$/;
const DECIMAL_BIGINT_RE = /^(?:0|[1-9][0-9]*)$/;
const ISO_WITH_OFFSET_RE =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,}))?(Z|[+-]\d{2}:\d{2})$/;

const MAX_ENTITY_ID_LENGTH = 256;
const MAX_ANCHOR_REFERENCE_LENGTH = 256;
const MAX_ANCHOR_TEXT_LENGTH = 250_000;
const MAX_ANCHOR_VERSES = 176;
const MAX_BOOKMARK_NOTE_LENGTH = 250_000;
const MAX_PRAYER_RAW_TEXT_LENGTH = 250_000;
const MAX_PRAYER_SUMMARY_LENGTH = 50_000;
const MAX_PRAYER_TESTIMONY_LENGTH = 250_000;
const MAX_PARSED_PERSON_LENGTH = 10_000;
const MAX_PARSED_CORE_PRAYER_LENGTH = 50_000;
const MAX_CARD_LABEL_LENGTH = 1_000;
const MAX_MENTIONED_PEOPLE = 256;
const MAX_PROGRESS_ENTRIES = 4_096;
const MAX_PROGRESS_NOTE_LENGTH = 50_000;
const MAX_VERSE_RESULT_ENTRIES = 1_024;
const MAX_VERSE_RESULT_FIELD_LENGTH = 1_024;
const MAX_HIGHLIGHT_METADATA_LENGTH = 10_000;
const CANONICAL_BOOKS_BY_SLUG = new Map(
  CANONICAL_BOOKS.map((book) => [book.slug, book] as const),
);

type DocumentValidationPolicy = 'strict-authoring' | 'accepted-v1';

function canonicalVerseRanges(verses: readonly number[]): string {
  const ranges: string[] = [];
  let start = verses[0];
  let end = verses[0];
  for (let index = 1; index < verses.length; index += 1) {
    const verse = verses[index];
    if (verse === end + 1) {
      end = verse;
    } else {
      ranges.push(start === end ? `${start}` : `${start}-${end}`);
      start = verse;
      end = verse;
    }
  }
  ranges.push(start === end ? `${start}` : `${start}-${end}`);
  return ranges.join(', ');
}

type JsonPrimitive = null | boolean | number | string;
export type JsonValue = JsonPrimitive | JsonValue[] | { [key: string]: JsonValue };

export class ContentContractError extends Error {
  readonly code: string;

  constructor(code: string, detail?: string) {
    super(detail ? `${code}: ${detail}` : code);
    this.name = 'ContentContractError';
    this.code = code;
  }
}

function fail(code: string, detail?: string): never {
  throw new ContentContractError(code, detail);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

export function hasExactKeys(
  value: unknown,
  keys: readonly string[],
): value is Record<string, unknown> {
  if (!isRecord(value)) return false;
  const actual = Object.keys(value).sort();
  const expected = [...keys].sort();
  return actual.length === expected.length && actual.every((key, i) => key === expected[i]);
}

function assertExactKeys(value: unknown, keys: readonly string[], label: string): asserts value is Record<string, unknown> {
  if (!hasExactKeys(value, keys)) fail('malformed-envelope', `${label} closed shape`);
}

function canonicalJsonInner(value: unknown, seen: Set<object>): string {
  if (value === null) return 'null';
  if (typeof value === 'string' || typeof value === 'boolean') return JSON.stringify(value);
  if (typeof value === 'number') {
    if (!Number.isSafeInteger(value)) fail('cj1-invalid-number');
    return JSON.stringify(value);
  }
  if (typeof value !== 'object') fail('cj1-non-json-value');
  if (seen.has(value)) fail('cj1-cycle');
  seen.add(value);
  try {
    if (Array.isArray(value)) {
      const parts: string[] = [];
      for (let i = 0; i < value.length; i += 1) {
        if (!Object.prototype.hasOwnProperty.call(value, i)) fail('cj1-array-hole');
        parts.push(canonicalJsonInner(value[i], seen));
      }
      return `[${parts.join(',')}]`;
    }
    if (!isRecord(value)) fail('cj1-non-plain-object');
    const keys = Object.keys(value).sort();
    return `{${keys
      .map((key) => `${JSON.stringify(key)}:${canonicalJsonInner(value[key], seen)}`)
      .join(',')}}`;
  } finally {
    seen.delete(value);
  }
}

export function cj1(value: unknown): string {
  return canonicalJsonInner(value, new Set());
}

export function cj1Bytes(value: unknown): Uint8Array {
  return encoder.encode(cj1(value));
}

export function bytesEqual(left: Uint8Array, right: Uint8Array): boolean {
  if (left.byteLength !== right.byteLength) return false;
  for (let i = 0; i < left.byteLength; i += 1) {
    if (left[i] !== right[i]) return false;
  }
  return true;
}

export function decodeCj1Bytes(bytes: Uint8Array): unknown {
  const text = decoder.decode(bytes);
  let value: unknown;
  try {
    value = JSON.parse(text) as unknown;
  } catch {
    fail('malformed-envelope', 'pending bytes are not JSON');
  }
  if (cj1(value) !== text) fail('malformed-envelope', 'pending bytes are not CJ1');
  return value;
}

export async function sha256Hex(bytes: Uint8Array): Promise<HexSha256> {
  const digest = await crypto.subtle.digest('SHA-256', Uint8Array.from(bytes).buffer);
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
}

export async function contentMutationDigest(mutation: ContentMutationV1): Promise<HexSha256> {
  assertContentMutationV1(mutation);
  return sha256Hex(cj1Bytes(mutation));
}

export function isDecimalBigintString(value: unknown): value is string {
  return typeof value === 'string' && DECIMAL_BIGINT_RE.test(value);
}

export function incrementDecimalBigint(value: string): string {
  if (!isDecimalBigintString(value)) fail('invalid-mutation-sequence');
  return (BigInt(value) + 1n).toString(10);
}

export function isHexSha256(value: unknown): value is HexSha256 {
  return typeof value === 'string' && HEX_SHA256_RE.test(value);
}

export function isCanonicalUuid(value: unknown): value is string {
  return typeof value === 'string' && UUID_RE.test(value);
}

export function isUuidV4(value: unknown): value is string {
  return typeof value === 'string' && UUID_V4_RE.test(value);
}

export function mintUuidV4(): string {
  return crypto.randomUUID();
}

export function normalizeIsoTimestamp(value: string): string {
  const match = ISO_WITH_OFFSET_RE.exec(value);
  if (!match || match[6] === '60') fail('invalid-iso-timestamp');
  const instant = new Date(value);
  if (!Number.isFinite(instant.getTime())) fail('invalid-iso-timestamp');
  const normalized = instant.toISOString();
  if (normalized < '0001-01-01T00:00:00.000Z' || normalized > '9999-12-31T23:59:59.999Z') {
    fail('invalid-iso-timestamp');
  }
  return normalized;
}

export function isIsoTimestamp(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  try {
    return normalizeIsoTimestamp(value) === value;
  } catch {
    return false;
  }
}

function isPositiveRevision(value: unknown): value is number {
  return Number.isSafeInteger(value) && (value as number) > 0;
}

function isNonnegativeInteger(value: unknown): value is number {
  return Number.isSafeInteger(value) && (value as number) >= 0;
}

function isBoundedString(value: unknown, maximum: number): value is string {
  return typeof value === 'string' && Array.from(value).length <= maximum;
}

function isBoundedStringOrNull(value: unknown, maximum: number): value is string | null {
  return value === null || isBoundedString(value, maximum);
}

const ANCHOR_KEYS = [
  'bookSlug',
  'bookName',
  'chapter',
  'verses',
  'wholeChapter',
  'reference',
  'text',
  'translation',
] as const;

function assertCompleteScriptureAnchor(
  value: unknown,
  policy: DocumentValidationPolicy,
): asserts value is CompleteScriptureAnchor {
  assertExactKeys(value, ANCHOR_KEYS, 'scripture anchor');
  if (
    typeof value.bookSlug !== 'string' ||
    typeof value.bookName !== 'string' ||
    !isPositiveRevision(value.chapter) ||
    !Array.isArray(value.verses) ||
    !value.verses.every(isPositiveRevision) ||
    (value.wholeChapter !== null && typeof value.wholeChapter !== 'boolean') ||
    typeof value.reference !== 'string' ||
    typeof value.text !== 'string' ||
    value.translation !== 'BSB'
  ) {
    fail('malformed-envelope', 'scripture anchor values');
  }
  // Protocol v1 originally froze shape/type-correct BSB anchors without
  // canonicalizing their display labels or coordinates. Server-accepted v1
  // envelopes remain readable byte-for-byte; only newly authored mutations
  // use the stricter canonical contract below.
  if (policy === 'accepted-v1') return;
  const canonical = CANONICAL_BOOKS_BY_SLUG.get(value.bookSlug);
  const chapterVerseCount = bsbChapterVerseCount(value.bookSlug, value.chapter);
  const verses = value.verses as number[];
  const versesStrictlyAscending = verses.every(
    (verse, index) =>
      chapterVerseCount !== null &&
      verse <= chapterVerseCount &&
      (index === 0 || verse > verses[index - 1]),
  );
  const expectedReference = canonical
    ? value.wholeChapter === true
      ? `${canonical.name} ${value.chapter}`
      : `${canonical.name} ${value.chapter}:${canonicalVerseRanges(verses)}`
    : null;
  if (
    !canonical ||
    value.bookName !== canonical.name ||
    value.chapter > canonical.chapters ||
    chapterVerseCount === null ||
    verses.length > MAX_ANCHOR_VERSES ||
    !versesStrictlyAscending ||
    (value.wholeChapter === true ? verses.length !== 0 : verses.length === 0) ||
    value.reference !== expectedReference ||
    value.reference.length === 0 ||
    !isBoundedString(value.reference, MAX_ANCHOR_REFERENCE_LENGTH) ||
    value.text.length === 0 ||
    !isBoundedString(value.text, MAX_ANCHOR_TEXT_LENGTH)
  ) {
    fail('malformed-envelope', 'scripture anchor canonical bounds');
  }
}

const PRAYER_KEYS = [
  'id',
  'rawText',
  'parsed',
  'gardenSummary',
  'progressHistory',
  'status',
  'testimony',
  'isPrivate',
  'userEditedFields',
  'scriptureAnchor',
  'verseResult',
  'createdAt',
  'updatedAt',
  'answeredAt',
  'testimonyWrittenAt',
  'deletedAt',
] as const;

const BOOKMARK_KEYS = [
  'id',
  'bookSlug',
  'chapter',
  'note',
  'anchor',
  'createdAt',
  'updatedAt',
  'deletedAt',
] as const;

const HIGHLIGHT_KEYS = [
  'id',
  'bookSlug',
  'chapter',
  'verse',
  'family',
  'sublabel',
  'personWho',
  'fulfilledAt',
  'reference',
  'createdAt',
  'updatedAt',
  'deletedAt',
] as const;

function assertParsedPrayer(value: unknown, policy: DocumentValidationPolicy): void {
  if (value === null) return;
  assertExactKeys(
    value,
    ['who', 'core_prayer', 'suggestedStatus', 'mentionedPeople', 'cardLabel'],
    'parsed prayer',
  );
  if (
    typeof value.who !== 'string' ||
    typeof value.core_prayer !== 'string' ||
    (value.suggestedStatus !== null && value.suggestedStatus !== 'GodIsMoving') ||
    (value.mentionedPeople !== null &&
      (!Array.isArray(value.mentionedPeople) ||
        !value.mentionedPeople.every((entry) => typeof entry === 'string'))) ||
    (value.cardLabel !== null && typeof value.cardLabel !== 'string')
  ) {
    fail('malformed-envelope', 'parsed prayer values');
  }
  if (
    policy === 'strict-authoring' &&
    (!isBoundedString(value.who, MAX_PARSED_PERSON_LENGTH) ||
      !isBoundedString(value.core_prayer, MAX_PARSED_CORE_PRAYER_LENGTH) ||
      (Array.isArray(value.mentionedPeople) &&
        (value.mentionedPeople.length > MAX_MENTIONED_PEOPLE ||
          !value.mentionedPeople.every((entry) =>
            isBoundedString(entry, MAX_PARSED_PERSON_LENGTH)))) ||
      !isBoundedStringOrNull(value.cardLabel, MAX_CARD_LABEL_LENGTH))
  ) {
    fail('malformed-envelope', 'parsed prayer bounds');
  }
}

function assertProgressHistory(value: unknown, policy: DocumentValidationPolicy): void {
  if (
    !Array.isArray(value) ||
    (policy === 'strict-authoring' && value.length > MAX_PROGRESS_ENTRIES)
  ) {
    fail('malformed-envelope', 'progress history');
  }
  const durableIds = new Set<string>();
  for (const entry of value) {
    if (
      !hasExactKeys(entry, ['date', 'note', 'recommendedStage']) &&
      !hasExactKeys(entry, ['id', 'date', 'note', 'recommendedStage'])
    ) {
      fail('malformed-envelope', 'progress entry closed shape');
    }
    if (
      (Object.prototype.hasOwnProperty.call(entry, 'id') &&
        (typeof entry.id !== 'string' || entry.id.trim() === '')) ||
      !isIsoTimestamp(entry.date) ||
      typeof entry.note !== 'string' ||
      ![null, 'Still praying', 'God is moving'].includes(entry.recommendedStage as never)
    ) {
      fail('malformed-envelope', 'progress entry values');
    }
    if (
      policy === 'strict-authoring' &&
      ((typeof entry.id === 'string' && !isBoundedString(entry.id, MAX_ENTITY_ID_LENGTH)) ||
        !isBoundedString(entry.note, MAX_PROGRESS_NOTE_LENGTH))
    ) {
      fail('malformed-envelope', 'progress entry bounds');
    }
    if (typeof entry.id === 'string') {
      if (durableIds.has(entry.id)) {
        fail('malformed-envelope', 'duplicate progress entry id');
      }
      durableIds.add(entry.id);
    }
  }
}

function assertUserEdits(value: unknown): void {
  if (value === null) return;
  assertExactKeys(value, ['who', 'status'], 'user edit fields');
  if (![null, true].includes(value.who as never) || ![null, true].includes(value.status as never)) {
    fail('malformed-envelope', 'user edit values');
  }
}

function assertVerseResult(value: unknown, policy: DocumentValidationPolicy): void {
  if (value === null) return;
  assertExactKeys(value, ['entries', 'matchedAt'], 'verse result');
  if (
    !Array.isArray(value.entries) ||
    !isIsoTimestamp(value.matchedAt)
  ) {
    fail('malformed-envelope', 'verse result values');
  }
  if (policy === 'strict-authoring' && value.entries.length > MAX_VERSE_RESULT_ENTRIES) {
    fail('malformed-envelope', 'verse result bounds');
  }
  for (const entry of value.entries) {
    assertExactKeys(entry, ['ref', 'category'], 'verse result entry');
    if (
      typeof entry.ref !== 'string' ||
      typeof entry.category !== 'string'
    ) {
      fail('malformed-envelope', 'verse result entry values');
    }
    if (
      policy === 'strict-authoring' &&
      (!isBoundedString(entry.ref, MAX_VERSE_RESULT_FIELD_LENGTH) ||
        !isBoundedString(entry.category, MAX_VERSE_RESULT_FIELD_LENGTH))
    ) {
      fail('malformed-envelope', 'verse result entry bounds');
    }
  }
}

function assertCompletePrayer(
  value: unknown,
  policy: DocumentValidationPolicy,
): asserts value is CompletePrayerDocument {
  assertExactKeys(value, PRAYER_KEYS, 'prayer document');
  assertParsedPrayer(value.parsed, policy);
  assertProgressHistory(value.progressHistory, policy);
  assertUserEdits(value.userEditedFields);
  if (value.scriptureAnchor !== null) {
    assertCompleteScriptureAnchor(value.scriptureAnchor, policy);
  }
  assertVerseResult(value.verseResult, policy);
  if (
    typeof value.id !== 'string' ||
    typeof value.rawText !== 'string' ||
    (value.gardenSummary !== null && typeof value.gardenSummary !== 'string') ||
    !['Open', 'In Progress', 'Answered', 'Moving'].includes(value.status as never) ||
    (value.testimony !== null && typeof value.testimony !== 'string') ||
    typeof value.isPrivate !== 'boolean' ||
    !isIsoTimestamp(value.createdAt) ||
    !isIsoTimestamp(value.updatedAt) ||
    (value.answeredAt !== null && !isIsoTimestamp(value.answeredAt)) ||
    (value.testimonyWrittenAt !== null && !isIsoTimestamp(value.testimonyWrittenAt)) ||
    value.deletedAt !== null
  ) {
    fail('malformed-envelope', 'prayer document values');
  }
  if (
    policy === 'strict-authoring' &&
    (!isBoundedString(value.rawText, MAX_PRAYER_RAW_TEXT_LENGTH) ||
      !isBoundedStringOrNull(value.gardenSummary, MAX_PRAYER_SUMMARY_LENGTH) ||
      !isBoundedStringOrNull(value.testimony, MAX_PRAYER_TESTIMONY_LENGTH))
  ) {
    fail('malformed-envelope', 'prayer document bounds');
  }
}

function assertCompleteBookmark(
  value: unknown,
  policy: DocumentValidationPolicy,
): asserts value is CompleteBookmarkDocument {
  assertExactKeys(value, BOOKMARK_KEYS, 'bookmark document');
  assertCompleteScriptureAnchor(value.anchor, policy);
  if (
    typeof value.id !== 'string' ||
    typeof value.bookSlug !== 'string' ||
    !isPositiveRevision(value.chapter) ||
    typeof value.note !== 'string' ||
    !isIsoTimestamp(value.createdAt) ||
    !isIsoTimestamp(value.updatedAt) ||
    value.deletedAt !== null
  ) {
    fail('malformed-envelope', 'bookmark document values');
  }
  if (
    policy === 'strict-authoring' &&
    (value.id.length === 0 ||
      !isBoundedString(value.id, MAX_ENTITY_ID_LENGTH) ||
      !isBoundedString(value.note, MAX_BOOKMARK_NOTE_LENGTH) ||
      value.bookSlug !== value.anchor.bookSlug ||
      value.chapter !== value.anchor.chapter)
  ) {
    fail('malformed-envelope', 'bookmark document values');
  }
}

function assertCompleteHighlight(
  value: unknown,
  policy: DocumentValidationPolicy,
): asserts value is CompleteHighlightDocument {
  assertExactKeys(value, HIGHLIGHT_KEYS, 'highlight document');
  if (
    typeof value.id !== 'string' ||
    typeof value.bookSlug !== 'string' ||
    !isPositiveRevision(value.chapter) ||
    !isPositiveRevision(value.verse) ||
    !['promise', 'comfort', 'command', 'conviction', 'praise', 'character', 'plain'].includes(
      value.family as never,
    ) ||
    (value.sublabel !== null && typeof value.sublabel !== 'string') ||
    (value.personWho !== null && typeof value.personWho !== 'string') ||
    (value.fulfilledAt !== null && !isIsoTimestamp(value.fulfilledAt)) ||
    typeof value.reference !== 'string' ||
    !isIsoTimestamp(value.createdAt) ||
    !isIsoTimestamp(value.updatedAt) ||
    value.deletedAt !== null
  ) {
    fail('malformed-envelope', 'highlight document values');
  }
  if (policy === 'accepted-v1') return;
  const canonical = CANONICAL_BOOKS_BY_SLUG.get(value.bookSlug);
  const chapterVerseCount = bsbChapterVerseCount(value.bookSlug, value.chapter);
  if (
    !canonical ||
    value.id.length === 0 ||
    !isBoundedString(value.id, MAX_ENTITY_ID_LENGTH) ||
    !isBoundedStringOrNull(value.sublabel, MAX_HIGHLIGHT_METADATA_LENGTH) ||
    !isBoundedStringOrNull(value.personWho, MAX_HIGHLIGHT_METADATA_LENGTH) ||
    value.chapter > canonical.chapters ||
    chapterVerseCount === null ||
    value.verse > chapterVerseCount ||
    value.reference !== `${canonical.name} ${value.chapter}:${value.verse}` ||
    value.reference.length === 0 ||
    !isBoundedString(value.reference, MAX_ANCHOR_REFERENCE_LENGTH)
  ) {
    fail('malformed-envelope', 'highlight canonical bounds');
  }
}

function assertCompleteLiveDocumentWithPolicy(
  entityType: ContentEntityType,
  entityId: string,
  value: unknown,
  policy: DocumentValidationPolicy,
): asserts value is CompleteLiveDocument {
  if (entityType === 'prayer') assertCompletePrayer(value, policy);
  else if (entityType === 'bookmark') assertCompleteBookmark(value, policy);
  else assertCompleteHighlight(value, policy);
  if (
    value.id !== entityId ||
    value.id.length === 0 ||
    (policy === 'strict-authoring' &&
      (!isBoundedString(value.id, MAX_ENTITY_ID_LENGTH) ||
        !isBoundedString(entityId, MAX_ENTITY_ID_LENGTH)))
  ) fail('malformed-envelope', 'entity id mismatch');
  if (
    entityType === 'highlight' &&
    value.id !== `${(value as CompleteHighlightDocument).bookSlug}:${
      (value as CompleteHighlightDocument).chapter
    }:${(value as CompleteHighlightDocument).verse}`
  ) {
    fail('malformed-envelope', 'highlight client entity id mismatch');
  }
}

export function assertCompleteLiveDocument(
  entityType: ContentEntityType,
  entityId: string,
  value: unknown,
): asserts value is CompleteLiveDocument {
  assertCompleteLiveDocumentWithPolicy(entityType, entityId, value, 'strict-authoring');
}

const MUTATION_KEYS = [
  'schema',
  'protocolVersion',
  'ownerId',
  'entityType',
  'entityId',
  'opId',
  'opKind',
  'mutationSeq',
  'expectedBaseRevision',
  'payload',
] as const;

function assertCommonMutationFields(
  value: Record<string, unknown>,
  allowBaselineSequence: boolean,
): void {
  if (
    value.schema !== 'adia-content-mutation-v1' ||
    value.protocolVersion !== 1 ||
    !isCanonicalUuid(value.ownerId) ||
    !['prayer', 'bookmark', 'highlight'].includes(value.entityType as never) ||
    typeof value.entityId !== 'string' ||
    value.entityId === '' ||
    !(allowBaselineSequence && value.mutationSeq === '0'
      ? isCanonicalUuid(value.opId)
      : isUuidV4(value.opId)) ||
    !isDecimalBigintString(value.mutationSeq) ||
    (!allowBaselineSequence && value.mutationSeq === '0')
  ) {
    fail('malformed-envelope', 'mutation identity');
  }
}

function assertContentMutationV1WithPolicy(
  value: unknown,
  options: { allowBaselineSequence?: boolean },
  policy: DocumentValidationPolicy,
): asserts value is ContentMutationV1 {
  assertExactKeys(value, MUTATION_KEYS, 'content mutation');
  assertCommonMutationFields(value, options.allowBaselineSequence === true);
  const entityType = value.entityType as ContentEntityType;
  const entityId = value.entityId as string;
  if (value.opKind === 'delete') {
    if (!isPositiveRevision(value.expectedBaseRevision)) {
      fail('malformed-envelope', 'delete base');
    }
    assertExactKeys(
      value.payload,
      ['kind', 'predecessorLiveDigest', 'predecessorLiveSnapshot', 'tombstonedAt'],
      'tombstone intent',
    );
    if (
      value.payload.kind !== 'tombstone-intent' ||
      !isHexSha256(value.payload.predecessorLiveDigest) ||
      !isIsoTimestamp(value.payload.tombstonedAt)
    ) {
      fail('malformed-envelope', 'tombstone intent values');
    }
    assertCompleteLiveDocumentWithPolicy(
      entityType,
      entityId,
      value.payload.predecessorLiveSnapshot,
      policy,
    );
    return;
  }
  if (!['create', 'update', 'restore'].includes(value.opKind as never)) {
    fail('malformed-envelope', 'operation kind');
  }
  if (
    (value.opKind === 'create' && value.expectedBaseRevision !== null) ||
    (value.opKind !== 'create' && !isPositiveRevision(value.expectedBaseRevision))
  ) {
    fail('malformed-envelope', 'live base');
  }
  assertExactKeys(value.payload, ['kind', 'document'], 'live payload');
  if (value.payload.kind !== 'live') fail('malformed-envelope', 'live payload kind');
  assertCompleteLiveDocumentWithPolicy(entityType, entityId, value.payload.document, policy);
}

export function assertContentMutationV1(
  value: unknown,
  options: { allowBaselineSequence?: boolean } = {},
): asserts value is ContentMutationV1 {
  assertContentMutationV1WithPolicy(value, options, 'strict-authoring');
}

export function projectMutation(envelope: OrdinaryContentEnvelopeV1): ContentMutationV1 {
  const common = {
    schema: 'adia-content-mutation-v1' as const,
    protocolVersion: envelope.protocolVersion,
    ownerId: envelope.ownerId,
    entityType: envelope.entityType,
    entityId: envelope.entityId,
    opId: envelope.opId,
    mutationSeq: envelope.mutationSeq,
    expectedBaseRevision: envelope.expectedBaseRevision,
  };
  if (envelope.payload.kind === 'live') {
    return {
      ...common,
      opKind: envelope.opKind as LiveMutationV1['opKind'],
      payload: envelope.payload,
    };
  }
  return {
    ...common,
    opKind: 'delete',
    expectedBaseRevision: envelope.expectedBaseRevision as number,
    payload: {
      kind: 'tombstone-intent',
      predecessorLiveDigest: envelope.payload.predecessorLiveDigest,
      predecessorLiveSnapshot: envelope.payload.predecessorLiveSnapshot,
      tombstonedAt: envelope.payload.tombstonedAt,
    },
  };
}

const ENVELOPE_KEYS = [
  'protocolVersion',
  'ownerId',
  'contentRevision',
  'entityType',
  'entityId',
  'opId',
  'opKind',
  'mutationSeq',
  'expectedBaseRevision',
  'contentDigest',
  'payload',
] as const;

function assertAcceptedTombstone(
  value: unknown,
  envelope: Record<string, unknown>,
  policy: DocumentValidationPolicy,
): asserts value is AcceptedTombstoneV1 {
  assertExactKeys(
    value,
    [
      'kind',
      'ownerId',
      'entityType',
      'entityId',
      'contentRevision',
      'predecessorLiveDigest',
      'predecessorLiveSnapshot',
      'tombstonedAt',
    ],
    'accepted tombstone',
  );
  if (
    value.kind !== 'tombstone' ||
    value.ownerId !== envelope.ownerId ||
    value.entityType !== envelope.entityType ||
    value.entityId !== envelope.entityId ||
    value.contentRevision !== envelope.contentRevision ||
    !isHexSha256(value.predecessorLiveDigest) ||
    !isIsoTimestamp(value.tombstonedAt)
  ) {
    fail('malformed-envelope', 'accepted tombstone values');
  }
  assertCompleteLiveDocumentWithPolicy(
    envelope.entityType as ContentEntityType,
    envelope.entityId as string,
    value.predecessorLiveSnapshot,
    policy,
  );
}

async function assertOrdinaryContentEnvelopeV1WithPolicy(
  value: unknown,
  expectedOwner: string | undefined,
  documentPolicy: DocumentValidationPolicy,
): Promise<void> {
  assertExactKeys(value, ENVELOPE_KEYS, 'ordinary envelope');
  if (
    value.protocolVersion !== 1 ||
    !isCanonicalUuid(value.ownerId) ||
    (expectedOwner !== undefined && value.ownerId !== expectedOwner) ||
    !isPositiveRevision(value.contentRevision) ||
    !['prayer', 'bookmark', 'highlight'].includes(value.entityType as never) ||
    typeof value.entityId !== 'string' ||
    value.entityId === '' ||
    !(value.mutationSeq === '0' ? isCanonicalUuid(value.opId) : isUuidV4(value.opId)) ||
    !['create', 'update', 'delete', 'restore'].includes(value.opKind as never) ||
    !isDecimalBigintString(value.mutationSeq) ||
    !isHexSha256(value.contentDigest)
  ) {
    fail(
      expectedOwner !== undefined && value.ownerId !== expectedOwner
        ? 'owner-scope-mismatch'
        : 'malformed-envelope',
      'envelope identity',
    );
  }
  if (!isRecord(value.payload)) fail('malformed-envelope', 'envelope payload');
  if (value.payload.kind === 'live') {
    if (value.opKind === 'delete') fail('malformed-envelope', 'delete with live payload');
    assertExactKeys(value.payload, ['kind', 'document'], 'envelope live payload');
    assertCompleteLiveDocumentWithPolicy(
      value.entityType as ContentEntityType,
      value.entityId,
      value.payload.document,
      documentPolicy,
    );
  } else if (value.payload.kind === 'tombstone') {
    if (value.opKind !== 'delete' || !isPositiveRevision(value.expectedBaseRevision)) {
      fail('malformed-envelope', 'tombstone operation');
    }
    assertAcceptedTombstone(value.payload, value, documentPolicy);
  } else {
    fail('malformed-envelope', 'envelope payload kind');
  }
  const projected = projectMutation(value as OrdinaryContentEnvelopeV1);
  assertContentMutationV1WithPolicy(
    projected,
    { allowBaselineSequence: true },
    documentPolicy,
  );
  const digest = await sha256Hex(cj1Bytes(projected));
  if (digest !== value.contentDigest) fail('digest-mismatch');
}

export async function assertOrdinaryContentEnvelopeV1(
  value: unknown,
  expectedOwner?: string,
): Promise<void> {
  await assertOrdinaryContentEnvelopeV1WithPolicy(
    value,
    expectedOwner,
    'strict-authoring',
  );
}

export type EnvelopeClassification =
  | { kind: 'recognized'; envelope: OrdinaryContentEnvelopeV1 }
  | { kind: 'unsupported'; protocolVersion: number; raw: unknown }
  | { kind: 'quarantined'; reason: 'digest-mismatch' | 'owner-scope-mismatch' | 'malformed-envelope'; raw: unknown };

export async function classifyOrdinaryEnvelope(
  value: unknown,
  expectedOwner: string,
): Promise<EnvelopeClassification> {
  if (!isRecord(value) || !Number.isInteger(value.protocolVersion)) {
    return { kind: 'quarantined', reason: 'malformed-envelope', raw: value };
  }
  if (value.protocolVersion !== 1) {
    return { kind: 'unsupported', protocolVersion: value.protocolVersion as number, raw: value };
  }
  try {
    await assertOrdinaryContentEnvelopeV1WithPolicy(
      value,
      expectedOwner,
      'accepted-v1',
    );
    return { kind: 'recognized', envelope: value as OrdinaryContentEnvelopeV1 };
  } catch (error) {
    const code = error instanceof ContentContractError ? error.code : 'malformed-envelope';
    const reason =
      code === 'digest-mismatch' || code === 'owner-scope-mismatch'
        ? code
        : 'malformed-envelope';
    return { kind: 'quarantined', reason, raw: value };
  }
}

export async function assertContentOpReceiptV1(
  value: unknown,
  expectedOwner?: string,
): Promise<void> {
  assertExactKeys(value, ['receiptVersion', 'envelope', 'ackedAt'], 'content receipt');
  if (value.receiptVersion !== 1 || !isIsoTimestamp(value.ackedAt)) {
    fail('malformed-envelope', 'content receipt values');
  }
  await assertOrdinaryContentEnvelopeV1(value.envelope, expectedOwner);
}

export async function verifyDurablePendingTuple(
  pending: DurablePendingContentTupleV1,
): Promise<void> {
  if (
    !isCanonicalUuid(pending.ownerId) ||
    !isNonnegativeInteger(pending.authEpoch) ||
    !isCanonicalUuid(pending.generation) ||
    !['prayer', 'bookmark', 'highlight'].includes(pending.entityType) ||
    !['create', 'update', 'delete', 'restore'].includes(pending.opKind) ||
    !isDecimalBigintString(pending.mutationSeq) ||
    pending.mutationSeq === '0' ||
    !isUuidV4(pending.opId) ||
    !isHexSha256(pending.contentDigest)
  ) {
    fail('malformed-envelope', 'pending tuple identity');
  }
  const decoded = decodeCj1Bytes(pending.completePendingBytes);
  assertContentMutationV1(decoded);
  const digest = await sha256Hex(pending.completePendingBytes);
  if (digest !== pending.contentDigest) fail('digest-mismatch');
  if (
    pending.ownerId !== decoded.ownerId ||
    pending.entityType !== decoded.entityType ||
    pending.entityId !== decoded.entityId ||
    pending.opKind !== decoded.opKind ||
    pending.mutationSeq !== decoded.mutationSeq ||
    pending.opId !== decoded.opId ||
    pending.expectedBaseRevision !== decoded.expectedBaseRevision
  ) {
    fail('malformed-envelope', 'pending tuple binding');
  }
}

export async function verifyStagedConflictV1(value: StagedConflictV1): Promise<void> {
  assertExactKeys(
    value,
    [
      'recordVersion', 'ownerId', 'authEpoch', 'generation', 'entityType', 'entityId',
      'heldReason', 'winningContentRevision', 'winningContentDigest', 'rejectedMutation',
      'rejectedContentDigest', 'supersedingMutation', 'supersedingContentDigest',
      'rejectedExpectedBaseRevision', 'rejectedOpId', 'rejectedMutationSeq', 'observedAt',
    ],
    'staged conflict',
  );
  if (
    value.recordVersion !== 1 ||
    value.heldReason !== 'awaiting-contiguous-winner' ||
    !isPositiveRevision(value.winningContentRevision) ||
    !isHexSha256(value.winningContentDigest) ||
    !isHexSha256(value.rejectedContentDigest) ||
    !isIsoTimestamp(value.observedAt)
  ) {
    fail('malformed-envelope', 'staged conflict values');
  }
  assertContentMutationV1(value.rejectedMutation);
  if ((await contentMutationDigest(value.rejectedMutation)) !== value.rejectedContentDigest) {
    fail('digest-mismatch');
  }
  if ((value.supersedingMutation === null) !== (value.supersedingContentDigest === null)) {
    fail('malformed-envelope', 'staged successor pair');
  }
  if (value.supersedingMutation && value.supersedingContentDigest) {
    assertContentMutationV1(value.supersedingMutation);
    if ((await contentMutationDigest(value.supersedingMutation)) !== value.supersedingContentDigest) {
      fail('digest-mismatch');
    }
  }
}

export function assertConflictRecordClosedShape(value: ConflictRecordV1): void {
  assertExactKeys(
    value,
    [
      'recordVersion', 'ownerId', 'entityType', 'entityId', 'conflictReason', 'winningKind',
      'winningContentRevision', 'winningContentDigest', 'losingSource',
      'losingProtocolVersion', 'losingOpKind', 'losingOpId', 'losingMutationSeq',
      'losingExpectedBaseRevision', 'losingContentRevision', 'loserSelection',
      'losingPayload', 'losingContentDigest', 'rejectionSource',
      'rejectedExpectedBaseRevision', 'rejectedOpId', 'rejectedMutationSeq', 'createdAt',
    ],
    'conflict record',
  );
}

/**
 * A passage in its content-document form: exactly the eight keys, with an
 * absent `wholeChapter` as null. The one normalizer for the passage of a
 * prayer or journal document and of an audio registration
 * (audio-registry.ts registerDurableAudio): register_audio_capture_v1's
 * content_validate_anchor_strict_v1 accepts only this shape, and reconcile
 * compares the registered passage with the saved document's as jsonb.
 */
export function normalizeAnchor(anchor: ScriptureAnchor): CompleteScriptureAnchor {
  return {
    bookSlug: anchor.bookSlug,
    bookName: anchor.bookName,
    chapter: anchor.chapter,
    verses: [...anchor.verses],
    wholeChapter: anchor.wholeChapter ?? null,
    reference: anchor.reference,
    text: anchor.text,
    translation: anchor.translation,
  };
}

export function completePrayerDocument(prayer: Prayer): CompletePrayerDocument {
  const entryIds = progressEntryIds(prayer.id, prayer.progressHistory);
  return {
    id: prayer.id,
    rawText: prayer.rawText,
    parsed: prayer.parsed
      ? {
          who: prayer.parsed.who,
          core_prayer: prayer.parsed.core_prayer,
          suggestedStatus: prayer.parsed.suggestedStatus,
          mentionedPeople: prayer.parsed.mentionedPeople ? [...prayer.parsed.mentionedPeople] : null,
          cardLabel: prayer.parsed.cardLabel ?? null,
        }
      : null,
    gardenSummary: prayer.gardenSummary ?? null,
    progressHistory: prayer.progressHistory.map((entry, index) => ({
      id: entryIds[index],
      date: normalizeIsoTimestamp(entry.date),
      note: entry.note,
      recommendedStage: entry.recommendedStage ?? null,
    })),
    status: prayer.status,
    testimony: prayer.testimony ?? null,
    isPrivate: prayer.isPrivate ?? false,
    userEditedFields: prayer.userEditedFields
      ? {
          who: prayer.userEditedFields.who ?? null,
          status: prayer.userEditedFields.status ?? null,
        }
      : null,
    scriptureAnchor: prayer.scriptureAnchor ? normalizeAnchor(prayer.scriptureAnchor) : null,
    verseResult: prayer.verseResult
      ? {
          entries: prayer.verseResult.entries.map((entry) => ({ ...entry })),
          matchedAt: normalizeIsoTimestamp(prayer.verseResult.matchedAt),
        }
      : null,
    createdAt: normalizeIsoTimestamp(prayer.createdAt),
    updatedAt: normalizeIsoTimestamp(prayer.updatedAt),
    answeredAt: prayer.answeredAt ? normalizeIsoTimestamp(prayer.answeredAt) : null,
    testimonyWrittenAt: prayer.testimonyWrittenAt
      ? normalizeIsoTimestamp(prayer.testimonyWrittenAt)
      : null,
    deletedAt: null,
  };
}

export function completeBookmarkDocument(bookmark: BibleBookmark): CompleteBookmarkDocument {
  return {
    id: bookmark.id,
    bookSlug: bookmark.bookSlug,
    chapter: bookmark.chapter,
    note: bookmark.note ?? '',
    anchor: normalizeAnchor(bookmark.anchor),
    createdAt: normalizeIsoTimestamp(bookmark.createdAt),
    updatedAt: normalizeIsoTimestamp(bookmark.updatedAt),
    deletedAt: null,
  };
}

export function completeHighlightDocument(highlight: BibleHighlight): CompleteHighlightDocument {
  return {
    id: highlight.id,
    bookSlug: highlight.bookSlug,
    chapter: highlight.chapter,
    verse: highlight.verse,
    family: highlight.family,
    sublabel: highlight.sublabel ?? null,
    personWho: highlight.personWho ?? null,
    fulfilledAt: highlight.fulfilledAt ? normalizeIsoTimestamp(highlight.fulfilledAt) : null,
    reference: highlight.reference,
    createdAt: normalizeIsoTimestamp(highlight.createdAt),
    updatedAt: normalizeIsoTimestamp(highlight.updatedAt),
    deletedAt: null,
  };
}

export function prayerFromCompleteDocument(
  document: CompletePrayerDocument,
  localOnly?: Prayer,
): Prayer {
  const pendingEnrichment = (() => {
    const pending = localOnly?.pendingEnrichment;
    if (!pending || localOnly?.updatedAt !== document.updatedAt) return null;
    if (typeof pending === 'string') return pending;
    return pending.sourceUpdatedAt === document.updatedAt ? pending : null;
  })();
  return {
    id: document.id,
    rawText: document.rawText,
    parsed: document.parsed
      ? {
          who: document.parsed.who,
          core_prayer: document.parsed.core_prayer,
          suggestedStatus: document.parsed.suggestedStatus,
          mentionedPeople: document.parsed.mentionedPeople ?? undefined,
          cardLabel: document.parsed.cardLabel,
        }
      : null,
    gardenSummary: document.gardenSummary ?? undefined,
    progressHistory: document.progressHistory.map((entry) => ({
      ...(entry.id ? { id: entry.id } : {}),
      date: entry.date,
      note: entry.note,
      recommendedStage: entry.recommendedStage,
    })),
    status: document.status,
    testimony: document.testimony,
    isPrivate: document.isPrivate,
    userEditedFields: document.userEditedFields
      ? {
          ...(document.userEditedFields.who ? { who: true as const } : {}),
          ...(document.userEditedFields.status ? { status: true as const } : {}),
        }
      : undefined,
    scriptureAnchor: document.scriptureAnchor
      ? {
          ...document.scriptureAnchor,
          wholeChapter: document.scriptureAnchor.wholeChapter ?? undefined,
          verses: [...document.scriptureAnchor.verses],
        }
      : null,
    verseResult: document.verseResult
      ? { ...document.verseResult, entries: document.verseResult.entries.map((entry) => ({ ...entry })) }
      : null,
    createdAt: document.createdAt,
    updatedAt: document.updatedAt,
    answeredAt: document.answeredAt,
    testimonyWrittenAt: document.testimonyWrittenAt,
    syncedAt: document.updatedAt,
    pendingEnrichment,
    pendingTranscriptionId: localOnly?.pendingTranscriptionId,
    transcriptionFailed: localOnly?.transcriptionFailed,
  };
}

export function bookmarkFromCompleteDocument(
  document: CompleteBookmarkDocument,
): BibleBookmark {
  return {
    ...document,
    anchor: {
      ...document.anchor,
      wholeChapter: document.anchor.wholeChapter ?? undefined,
      verses: [...document.anchor.verses],
    },
    syncedAt: document.updatedAt,
    deletedAt: null,
  };
}

export function highlightFromCompleteDocument(
  document: CompleteHighlightDocument,
): BibleHighlight {
  return {
    ...document,
    syncedAt: document.updatedAt,
    deletedAt: null,
  };
}

export function envelopeBytes(envelope: OrdinaryContentEnvelopeV1): Uint8Array {
  return cj1Bytes(envelope);
}

export function envelopeBytesEqual(
  left: OrdinaryContentEnvelopeV1,
  right: OrdinaryContentEnvelopeV1,
): boolean {
  return bytesEqual(envelopeBytes(left), envelopeBytes(right));
}
